import * as React from "react";
import { Form, useFormikContext } from "formik";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import { SelectOption } from "@mui/base";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { SwitchField } from "components/formikFields/SwitchField/SwitchField";
import { SelectOneField } from "components/formikFields/SelectOneField/SelectOneField";
import { SelectOneAutocompleteField } from "components/formikFields/SelectOneAutocompleteField/SelectOneAutocompleteField";
import { useQuery } from "react-query";
import { PowerBiReportSummary, PowerBiWorkspace, UtilsService } from "gen/clients";

enum FieldName {
  NAME = "name",
  DESCRIPTION = "description",
  WORKSPACE_ID = "workspaceId",
  REPORT_ID = "reportId",
  ENABLE_RLS = "enableRls",
  DATASET_ID = "datasetId",
  RLS_ROLES = "rlsRoles",
  RLS_USERNAME = "rlsUsername"
}

interface FormValues {
  [FieldName.NAME]: string;
  [FieldName.DESCRIPTION]: string;
  [FieldName.WORKSPACE_ID]: string;
  [FieldName.REPORT_ID]: SelectOption<string> | null;
  [FieldName.ENABLE_RLS]: boolean;
  [FieldName.DATASET_ID]: string;
  [FieldName.RLS_ROLES]: string;
  [FieldName.RLS_USERNAME]: string;
}

interface Props {
  onCancel: () => void;
  initialReportId?: string;
}

const PowerBiReportForm: React.FC<Props> = ({ onCancel, initialReportId }) => {
  const { isSubmitting, values, setFieldValue } = useFormikContext<FormValues>();
  const rlsEnabled = values[FieldName.ENABLE_RLS];

  const workspaceId = values[FieldName.WORKSPACE_ID].trim();
  const workspaceSelected = !!workspaceId;

  const {
    data: workspaces = [],
    isLoading: isLoadingWorkspaces,
    isError: isWorkspacesError,
    isSuccess: isWorkspacesSuccess
  } = useQuery<PowerBiWorkspace[]>(["listPowerBiWorkspaces"], {
    queryFn: UtilsService.listPowerBiWorkspaces,
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000
  });

  const {
    data: reports = [],
    isLoading: isLoadingReports,
    isError: isReportsError,
    isSuccess: isReportsSuccess
  } = useQuery<PowerBiReportSummary[]>(["listPowerBiReports", workspaceId], {
    enabled: workspaceSelected,
    queryFn: () =>
      UtilsService.listPowerBiReports({
        workspaceId: workspaceId || ""
      }),
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000
  });

  const initialReportAppliedRef = React.useRef(false);
  const selectedReportId = values[FieldName.REPORT_ID]?.value;

  const workspacesErrorMessage = React.useMemo(() => {
    if (isLoadingWorkspaces) {
      return null;
    }
    if (isWorkspacesError) {
      return "Failed to fetch workspaces. Please check the Power BI credentials configuration.";
    }
    if (isWorkspacesSuccess && workspaces.length === 0) {
      return "No workspaces found";
    }
    return null;
  }, [isLoadingWorkspaces, isWorkspacesError, isWorkspacesSuccess, workspaces.length]);

  const reportsErrorMessage = React.useMemo(() => {
    if (!workspaceSelected) {
      return null;
    }
    if (isLoadingReports) {
      return null;
    }
    if (isReportsError) {
      return "Failed to fetch reports. Please check the Power BI credentials configuration.";
    }
    if (isReportsSuccess && reports.length === 0) {
      return "No reports found in this workspace";
    }
    return null;
  }, [isLoadingReports, isReportsError, isReportsSuccess, reports.length, workspaceSelected]);

  // Reset dependent fields when workspace changes
  const handleWorkspaceChange = React.useCallback(
    (_workspaceId: string) => {
      // Clear report selection and datasetId when workspace changes
      setFieldValue(FieldName.REPORT_ID, null);
      setFieldValue(FieldName.DATASET_ID, "");
    },
    [setFieldValue]
  );

  // Apply initial report selection once reports are loaded (edit mode)
  React.useEffect(() => {
    if (!initialReportId || initialReportAppliedRef.current || !workspaceSelected || !isReportsSuccess) {
      return;
    }

    if (selectedReportId) {
      initialReportAppliedRef.current = true;
      return;
    }

    const matchingReport = reports.find(r => r.id === initialReportId);
    if (matchingReport) {
      setFieldValue(FieldName.REPORT_ID, { value: matchingReport.id, label: matchingReport.name });
      setFieldValue(FieldName.DATASET_ID, matchingReport.datasetId);
    }
    initialReportAppliedRef.current = true;
  }, [initialReportId, isReportsSuccess, reports, selectedReportId, setFieldValue, workspaceSelected]);

  // Handle report selection change to update datasetId
  const handleReportChange = React.useCallback(
    (option: SelectOption<string> | null) => {
      if (option?.value) {
        const selectedReport = reports.find(r => r.id === option.value);
        if (selectedReport) {
          setFieldValue(FieldName.DATASET_ID, selectedReport.datasetId);
        }
      } else {
        setFieldValue(FieldName.DATASET_ID, "");
      }
    },
    [reports, setFieldValue]
  );

  const sortedWorkspaceOptions = React.useMemo(
    () => [...workspaces].sort((a, b) => a.name.localeCompare(b.name)).map(w => ({ value: w.id, label: w.name })),
    [workspaces]
  );

  const sortedReportOptions = React.useMemo(
    () => [...reports].sort((a, b) => a.name.localeCompare(b.name)).map(r => ({ value: r.id, label: r.name })),
    [reports]
  );

  const getWorkspaceHelperText = () => {
    if (isLoadingWorkspaces) {
      return "Loading workspaces...";
    }
    return workspacesErrorMessage || "Select a Power BI workspace";
  };

  const getReportHelperText = () => {
    if (!workspaceSelected) {
      return "Select a workspace first";
    }
    if (isLoadingReports) {
      return "Loading reports...";
    }
    return reportsErrorMessage || "Select a report from the workspace";
  };

  return (
    <Form noValidate={true}>
      {(isLoadingWorkspaces || isLoadingReports) && <LinearProgress sx={{ mb: 4 }} />}
      <Grid container={true} spacing={2}>
        <Grid item={true} xs={12}>
          <TextInputField name={FieldName.NAME} label="Report Name" required={true} />
        </Grid>
        <Grid item={true} xs={12}>
          <TextInputField name={FieldName.DESCRIPTION} label="Description" />
        </Grid>
        <Grid item={true} xs={12}>
          <SelectOneField
            name={FieldName.WORKSPACE_ID}
            label="Workspace"
            required={true}
            options={sortedWorkspaceOptions}
            disabled={isLoadingWorkspaces}
            isLoading={isLoadingWorkspaces}
            isError={!!workspacesErrorMessage}
            helperText={getWorkspaceHelperText()}
            onChange={handleWorkspaceChange}
          />
        </Grid>
        <Grid item={true} xs={12}>
          <SelectOneAutocompleteField
            name={FieldName.REPORT_ID}
            label="Report"
            required={true}
            options={sortedReportOptions}
            areOptionsLoading={isLoadingReports}
            disabled={!workspaceSelected || isLoadingReports}
            isError={!!reportsErrorMessage}
            helperText={getReportHelperText()}
            onSelect={handleReportChange}
          />
        </Grid>
        <Grid item={true} xs={12}>
          <SwitchField name={FieldName.ENABLE_RLS} label="Enable Row Level Security (optional)" />
        </Grid>
        {rlsEnabled && (
          <>
            <Grid item={true} xs={12}>
              <TextInputField
                name={FieldName.DATASET_ID}
                label="Dataset IDs"
                helperText="Comma-separated list of dataset IDs for RLS."
                required={true}
                disabled={true}
              />
            </Grid>
            <Grid item={true} xs={12}>
              <TextInputField
                name={FieldName.RLS_ROLES}
                label="RLS Roles"
                helperText="Comma-separated roles (optional)."
              />
            </Grid>
            <Grid item={true} xs={12}>
              <TextInputField
                name={FieldName.RLS_USERNAME}
                label="RLS Username Override"
                helperText="Optional username override."
              />
            </Grid>
          </>
        )}
      </Grid>

      <Box sx={{ mt: 5 }}>
        <LoadingButton isLoading={isSubmitting}>Save</LoadingButton>
        <Button color="secondary" onClick={onCancel} disabled={isSubmitting} sx={{ ml: 1 }}>
          Cancel
        </Button>
      </Box>
    </Form>
  );
};

export { PowerBiReportForm, FieldName };
